import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

@customElement("cg-visitor-form")
export class CGVisitorForm extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
    }

    .form-wrap {
      background: var(--cg-surface);
      border: 1px solid var(--cg-border);
      border-radius: 12px;
      padding: 16px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
    }

    :host([variant="inline"]) .form-wrap {
      margin-top: 8px;
      margin-bottom: 8px;
      border-radius: 0 12px 12px 12px;
      background: var(--cg-bubble-agent-bg, var(--cg-surface));
    }

    .title {
      font-size: 14px;
      font-weight: 600;
      color: var(--cg-text);
      margin-bottom: 4px;
    }

    .subtitle {
      font-size: 13px;
      color: var(--cg-muted);
      margin-bottom: 16px;
    }

    .field {
      margin-bottom: 12px;
    }

    label {
      display: block;
      font-size: 12px;
      font-weight: 500;
      color: var(--cg-text);
      margin-bottom: 6px;
    }

    input {
      width: 100%;
      box-sizing: border-box;
      padding: 10px 12px;
      border: 1px solid var(--cg-border);
      border-radius: 8px;
      background: color-mix(in srgb, var(--cg-bg) 98%, black);
      color: var(--cg-text);
      font-size: 14px;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;
    }

    input:focus {
      border-color: var(--cg-primary);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--cg-primary) 20%, transparent);
    }

    input::placeholder {
      color: var(--cg-muted);
      opacity: 0.6;
    }

    button {
      width: 100%;
      padding: 10px;
      margin-top: 8px;
      background: var(--cg-primary);
      color: var(--cg-on-primary);
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: opacity 0.2s, transform 0.1s;
    }

    button:hover {
      opacity: 0.9;
    }

    button:active {
      transform: scale(0.98);
    }

    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  `;

  @property({ type: String, reflect: true }) variant: "inline" | "full" = "full";

  @state() private name = "";
  @state() private email = "";
  @state() private submitting = false;
  @state() private submitted = false;

  private handleSubmit(e: Event) {
    e.preventDefault();
    if (!this.name && !this.email) return;

    this.submitting = true;
    
    this.dispatchEvent(
      new CustomEvent("submit-details", {
        detail: { name: this.name, email: this.email },
        bubbles: true,
        composed: true,
      })
    );

    // Provide a small delay for visual feedback, then convert to checked state if needed or hide.
    setTimeout(() => {
      this.submitting = false;
      this.submitted = true;
    }, 600);
  }

  override render() {
    if (this.submitted) {
      if (this.variant === "inline") {
        return html`
          <div class="form-wrap" style="padding: 12px; display: flex; align-items: center; gap: 8px;">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--cg-primary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><path d="m9 11 3 3L22 4"></path></svg>
            <span style="font-size: 13px; color: var(--cg-text); font-weight: 500;">Details saved</span>
          </div>
        `;
      }
      return nothing; // Will be navigated away in full screen anyway
    }

    return html`
      <div class="form-wrap">
        <div class="title">Need more details?</div>
        <div class="subtitle">Please provide your info so we can reach you better.</div>
        
        <form @submit=${this.handleSubmit}>
          <div class="field">
            <label for="name">Name</label>
            <input 
              id="name" 
              type="text" 
              placeholder="John Doe" 
              .value=${this.name} 
              @input=${(e: Event) => this.name = (e.target as HTMLInputElement).value}
              ?disabled=${this.submitting}
            />
          </div>
          
          <div class="field">
            <label for="email">Email</label>
            <input 
              id="email" 
              type="email" 
              placeholder="john@example.com" 
              .value=${this.email} 
              @input=${(e: Event) => this.email = (e.target as HTMLInputElement).value}
              ?disabled=${this.submitting}
            />
          </div>
          
          <button type="submit" ?disabled=${this.submitting || (!this.name && !this.email)}>
            ${this.submitting ? "Saving..." : "Save details"}
          </button>
        </form>
      </div>
    `;
  }
}
